import useAxios from "axios-hooks";
import React, { Suspense, useEffect, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import {
  Button,
  Flex,
  Box,
  Heading,
  Icon,
  Text,
  CircularProgress,
  CircularProgressLabel,
  HStack,
  VStack,
  Tooltip,
  useColorModeValue,
} from "@chakra-ui/react";
import styled from "@emotion/styled";
import {
  MdStop,
  MdElectricBolt,
  MdBolt,
  MdBattery90,
  MdPower,
  MdUpdate,
  MdCable,
  MdOutlineElectricCar,
} from "react-icons/md";
import { motion } from "framer-motion";

import { toastState } from "atoms/toastState";
import ChargingSessionCard from "../../../../components/card/ChargingSessionCard";
import LoadingSpinner from "components/feedback/LoadingSpinner";
import { activeConnectorState } from "atoms/activeConnectorState";
import logger from "infra/logger";
import { loggedInUserState } from "atoms/loggedInUserState";
import { accessLevels } from "utils/accessLevels";

// Glass morphism styled components
const GlassMorphismCard = styled(Box)`
  backdrop-filter: blur(12px);
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(35, 169, 121, 0.15);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      rgba(35, 169, 121, 0.1),
      rgba(35, 169, 121, 0.6),
      rgba(35, 169, 121, 0.1)
    );
    z-index: 1;
  }

  &:hover {
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`;

const StatCard = styled(Box)`
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(8px);
  border-radius: 12px;
  padding: 1rem;
  transition: all 0.3s ease;
  border: 1px solid rgba(35, 169, 121, 0.1);

  &:hover {
    background: rgba(255, 255, 255, 0.7);
    transform: translateY(-2px);
  }
`;

const PulseButton = styled(Button)`
  position: relative;
  overflow: hidden;
  transform: translateZ(0);

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.2);
    transform: scale(0);
    opacity: 0;
    border-radius: inherit;
  }

  &:hover:before {
    animation: pulse 0.8s ease-out;
  }

  @keyframes pulse {
    0% {
      transform: scale(0);
      opacity: 0.5;
    }
    100% {
      transform: scale(2);
      opacity: 0;
    }
  }

  &:hover {
    transform: translateY(-2px);
  }
`;

const GlowingIcon = styled(Icon)`
  filter: drop-shadow(0 0 3px rgba(35, 169, 121, 0.6));
`;

const AnimatedCircularProgress = styled(CircularProgress)`
  transition: all 0.5s ease-out;
  filter: drop-shadow(0 0 5px rgba(35, 169, 121, 0.3));
`;

const OngoingSession = ({ refresh }) => {
  const activeConnector = useRecoilValue(activeConnectorState);
  const loggedInUser = useRecoilValue(loggedInUserState);
  const role = loggedInUser?.role;
  const accessLevel = accessLevels[role]?.accessLevel;

  const setToast = useSetRecoilState(toastState);
  const [session, setSession] = useState(null);
  const [{ data, loading, error }, execute] = useAxios(
    {
      method: "GET",
      url: `/api/chargingSession/ongoing?evseUid=${activeConnector?.EvseUid}`,
    },
    { manual: true }
  );
  const [
    { data: stopData, loading: stopLaoding, error: stopError },
    executeStop,
  ] = useAxios(
    {
      method: "POST",
      url: `/api/ocpp16/charging/stop`,
    },
    { manual: true }
  );
  const getOngoingSesson = async () => {
    try {
      const response = await execute();
      setSession(response.data.data);
    } catch (error) {
      const err = error?.response?.data?.error;
      logger.log(error);
    }
  };
  const stopSession = async () => {
    var payload = {};
    payload["StationId"] = activeConnector.EvseId;
    payload["TransactionId"] = session.Id;
    try {
      const response = await executeStop({ data: payload });
      setToast({
        isOpen: true,
        title: "Session Stopped",
        status: "success",
      });
    } catch (error) {
      logger.log(error);
      const err = error?.response?.data?.error;
      logger.log(error);
      setToast({
        isOpen: true,
        title: "Error occured when stopping Session",
        description: err ? err : null,
        status: "error",
      });
    }
    refresh();
  };
  useEffect(() => {
    if (activeConnector?.Status === "Charging") {
      getOngoingSesson();
      const intervalId = setInterval(() => {
        getOngoingSesson();
      }, 10000);
      return () => clearInterval(intervalId);
    }
  }, []);
  // Theme colors
  const labelColor = useColorModeValue("gray.700", "gray.200");
  const iconColor = useColorModeValue("#23A979", "#ff9966");

  // Calculate a mock battery percentage for demo purposes
  const batteryPercentage = session ? session?.ConsumerSoc : 0;

  // Calculate energy consumed (mock data if not available)
  const energyConsumed = session ? session?.UnitsConsumed : 0;

  // Animation speeds - faster during loading
  const orbitSpeed1 = loading ? 4 : 8; // First orbit speed (seconds)
  const orbitSpeed2 = loading ? 6 : 12; // Second orbit speed (seconds)

  return (
    <GlassMorphismCard
      width="100%"
      position="relative"
      p={{ base: 3, md: 4 }}
      as={motion.div}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Flex justify="space-between" align="center" mb={4}>
        <HStack spacing={2}>
          <GlowingIcon as={MdElectricBolt} color="brand.500" boxSize={{ base: 5, md: 6 }} />
          <Heading size={{ base: "sm", md: "md" }} fontWeight={600} color={labelColor}>
            Ongoing Charging Session
          </Heading>
        </HStack>

        {session && (
          <Tooltip label="Session active" placement="top">
            <Box>
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
              >
                <Icon as={MdPower} color="brand.500" boxSize={5} opacity={0.8} />
              </motion.div>
            </Box>
          </Tooltip>
        )}
      </Flex>

      {session ? (
        <Box position="relative" overflow="hidden">
          {/* Background gradient effect */}
          {/* <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bgGradient="radial(circle at 50% 30%, rgba(35, 169, 121, 0.08), transparent 70%)"
            zIndex="0"
            as={motion.div}
            animate={{
              opacity: [0.5, 0.8, 0.5],
              scale: [1, 1.05, 1],
            }}
            transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
          /> */}

          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align="center"
            gap={6}
            position="relative"
            zIndex="1"
          >
            {/* Right side - Stats with enhanced glass morphism */}
            <Flex
              direction={{ base: "column", md: "row" }}
              gap={{ base: 3, md: 4 }}
              width="100%"
              justify="center"
              as={motion.div}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {/* Consumption Stat */}
              <Box
                bg="rgba(255, 255, 255, 0.7)"
                backdropFilter="blur(12px)"
                borderRadius="xl"
                p={{ base: 3, md: 5 }}
                flex="1"
                width={{ base: "100%", md: "auto" }}
                border="1px solid rgba(35, 169, 121, 0.15)"
                boxShadow="0 8px 32px rgba(0, 0, 0, 0.05)"
                as={motion.div}
                whileHover={{
                  y: -5,
                  boxShadow: "0 15px 30px rgba(35, 169, 121, 0.15)",
                }}
                position="relative"
                overflow="hidden"
                _after={{
                  content: '""',
                  position: "absolute",
                  top: "0",
                  right: "0",
                  width: "100%",
                  height: "100%",
                  background:
                    "linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 50%)",
                  zIndex: 0,
                }}
              >
                <VStack
                  spacing={2}
                  align="center"
                  zIndex="1"
                  position="relative"
                >
                  <Text
                    fontSize={{ base: "xs", md: "sm" }}
                    color="gray.500"
                    fontWeight="600"
                    letterSpacing="1px"
                    textTransform="uppercase"
                    position="relative"
                    _after={{
                      content: '""',
                      position: "absolute",
                      bottom: "-4px",
                      left: "0",
                      width: "100%",
                      height: "2px",
                      background:
                        "linear-gradient(90deg, #23A979, transparent)",
                    }}
                  >
                    CONSUMPTION
                  </Text>
                  <HStack align="baseline" mt={2}>
                    <Heading size={{ base: "md", md: "lg" }} color="brand.500" fontWeight="bold">
                      {energyConsumed ?? "--"}
                    </Heading>
                    <Text fontSize={{ base: "sm", md: "md" }} color="gray.600" fontWeight="medium">
                      kWh
                    </Text>
                  </HStack>
                  <Box
                    as={motion.div}
                    animate={{
                      rotate: [0, 10, 0, -10, 0],
                      scale: [1, 1.2, 1],
                    }}
                    transition={{
                      repeat: Infinity,
                      duration: 5,
                      repeatType: "reverse",
                    }}
                    mt={1}
                  >
                    <Icon
                      as={MdBolt}
                      color="brand.500"
                      boxSize={8}
                      filter="drop-shadow(0 0 5px rgba(35, 169, 121, 0.7))"
                    />
                  </Box>
                </VStack>
              </Box>

              {/* Battery SOC Stat */}
              <Box
                bg="rgba(255, 255, 255, 0.7)"
                backdropFilter="blur(12px)"
                borderRadius="xl"
                p={{ base: 3, md: 5 }}
                flex="1"
                width={{ base: "100%", md: "auto" }}
                border="1px solid rgba(35, 169, 121, 0.15)"
                boxShadow="0 8px 32px rgba(0, 0, 0, 0.05)"
                as={motion.div}
                whileHover={{
                  y: -5,
                  boxShadow: "0 15px 30px rgba(35, 169, 121, 0.15)",
                }}
                position="relative"
                overflow="hidden"
                _after={{
                  content: '""',
                  position: "absolute",
                  top: "0",
                  right: "0",
                  width: "100%",
                  height: "100%",
                  background:
                    "linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 50%)",
                  zIndex: 0,
                }}
              >
                <VStack
                  spacing={2}
                  align="center"
                  zIndex="1"
                  position="relative"
                >
                  <Text
                    fontSize={{ base: "xs", md: "sm" }}
                    color="gray.500"
                    fontWeight="600"
                    letterSpacing="1px"
                    textTransform="uppercase"
                    position="relative"
                    _after={{
                      content: '""',
                      position: "absolute",
                      bottom: "-4px",
                      left: "0",
                      width: "100%",
                      height: "2px",
                      background:
                        "linear-gradient(90deg, #23A979, transparent)",
                    }}
                  >
                    BATTERY SOC
                  </Text>
                  <Box mt={2} position="relative">
                    <AnimatedCircularProgress
                      value={batteryPercentage ?? 0}
                      size={{ base: "70px", md: "90px" }}
                      thickness="8px"
                      color="brand.500"
                      trackColor="rgba(35, 169, 121, 0.1)"
                      capIsRound
                      as={motion.div}
                    >
                      <CircularProgressLabel
                        fontSize="xl"
                        fontWeight="bold"
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                      >
                        <Text>{batteryPercentage ?? "--"}%</Text>
                      </CircularProgressLabel>
                    </AnimatedCircularProgress>
                    <Box
                      position="absolute"
                      top="50%"
                      left="50%"
                      transform="translate(-50%, -50%)"
                      width="110%"
                      height="110%"
                      borderRadius="full"
                      bg="transparent"
                      border="1px solid rgba(35, 169, 121, 0.2)"
                      as={motion.div}
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{
                        repeat: Infinity,
                        duration: 3,
                        ease: "easeInOut",
                      }}
                    />
                  </Box>
                </VStack>
              </Box>
            </Flex>
          </Flex>
        </Box>
      ) : (
        <Box py={8} position="relative" overflow="hidden">
          <Flex
            direction="column"
            align="center"
            justify="center"
            position="relative"
            zIndex="1"
          >
            {/* Animated charging icon with orbiting elements */}
            <Box position="relative" height="120px" width="120px" mb={5}>
              {/* Pulsing circle background */}
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                height="100%"
                width="100%"
                borderRadius="full"
                bg="rgba(35, 169, 121, 0.1)"
                as={motion.div}
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.5, 0.3],
                }}
                transition={{
                  repeat: Infinity,
                  duration: 2.5,
                  ease: "easeInOut",
                }}
              />

              {/* First orbiting element - Cable */}
              <Box
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                as={motion.div}
                animate={{ rotate: 360 }}
                transition={{
                  repeat: Infinity,
                  duration: loading ? 4 : 8,
                  ease: "linear",
                }}
                zIndex="10"
              >
                <Box
                  position="absolute"
                  top="-5px"
                  left="50%"
                  transform="translateX(-50%)"
                  bg="white"
                  p={1}
                  borderRadius="full"
                  boxShadow="0 0 10px rgba(35, 169, 121, 0.5)"
                >
                  <Icon as={MdCable} color="brand.500" boxSize={4} />
                </Box>
              </Box>

              {/* Second orbiting element - Update */}
              <Box
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                as={motion.div}
                animate={{ rotate: -360 }}
                transition={{
                  repeat: Infinity,
                  duration: loading ? 6 : 12,
                  ease: "linear",
                }}
                zIndex="10"
              >
                <Box
                  position="absolute"
                  bottom="-5px"
                  left="50%"
                  transform="translateX(-50%)"
                  bg="white"
                  p={1}
                  borderRadius="full"
                  boxShadow="0 0 10px rgba(35, 169, 121, 0.5)"
                >
                  <Icon as={MdUpdate} color="brand.500" boxSize={4} />
                </Box>
              </Box>

              {/* Inner circle with car icon */}
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                height="70%"
                width="70%"
                borderRadius="full"
                bg="rgba(255, 255, 255, 0.9)"
                boxShadow="0 0 15px rgba(35, 169, 121, 0.3)"
                display="flex"
                alignItems="center"
                justifyContent="center"
                zIndex="5"
              >
                {/* Car icon */}
                <Box
                  as={motion.div}
                  animate={{ rotateY: [0, 180, 360] }}
                  transition={{
                    repeat: Infinity,
                    duration: 4,
                    ease: "easeInOut",
                  }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon as={MdOutlineElectricCar} color="brand.500" boxSize={8} />
                </Box>
              </Box>
            </Box>

            {/* Status text with glass effect container */}
            <Box
              bg="rgba(255, 255, 255, 0.7)"
              backdropFilter="blur(8px)"
              borderRadius="lg"
              px={6}
              py={3}
              border="1px solid rgba(35, 169, 121, 0.15)"
              boxShadow="0 4px 12px rgba(0, 0, 0, 0.05)"
              maxW="280px"
              w="100%"
              textAlign="center"
              as={motion.div}
              initial={{ y: 5, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.3 }}
              position="relative"
              _before={{
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "1px",
                background:
                  "linear-gradient(90deg, rgba(35, 169, 121, 0), rgba(35, 169, 121, 0.5), rgba(35, 169, 121, 0))",
              }}
            >
              <Heading
                size="sm"
                color="brand.500"
                fontWeight="600"
                mb={2}
                textAlign="center"
                letterSpacing="0.5px"
              >
                Waiting for data
              </Heading>

              <Text
                color="gray.600"
                fontWeight="medium"
                textAlign="center"
                fontSize="sm"
              >
                Waiting for the charger to update the session or gun status
              </Text>
            </Box>

            {/* Animated dots */}
            <HStack mt={4} spacing={2} justify="center">
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  h="8px"
                  w="8px"
                  borderRadius="full"
                  bg="brand.500"
                  boxShadow="0 0 8px rgba(35, 169, 121, 0.5)"
                  as={motion.div}
                  animate={{
                    opacity: [0.3, 1, 0.3],
                    scale: [0.8, 1.2, 0.8],
                  }}
                  transition={{
                    repeat: Infinity,
                    duration: 1.5,
                    delay: i * 0.2,
                    ease: "easeInOut",
                  }}
                />
              ))}
            </HStack>
          </Flex>
        </Box>
      )}

      {session && session.Status === "ONGOING" && (
        <Flex justify="center" mt={4}>
          <PulseButton
            bg="linear-gradient(90deg, #e53e3e 0%, #f56565 100%)"
            _hover={{
              boxShadow: "0 8px 16px rgba(229, 62, 62, 0.3)",
            }}
            _active={{
              transform: "translateY(0)",
              boxShadow: "0 4px 8px rgba(229, 62, 62, 0.3)",
            }}
            color="white"
            borderRadius="md"
            px={2}
            // height="45px"
            onClick={stopSession}
            isLoading={stopLaoding}
            leftIcon={<Icon as={MdStop} boxSize={5} />}
            fontWeight={600}
            fontSize="sm"
            transition="all 0.3s ease"
            as={motion.button}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
          >
            Stop Charging
          </PulseButton>
        </Flex>
      )}
    </GlassMorphismCard>
  );
};
export default OngoingSession;
